import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

// SVG icon helpers — 18×18, presentational attributes only
const svgBase = {
  viewBox: '0 0 24 24',
  width: 18,
  height: 18,
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconBox() {
  return (
    <svg {...svgBase}>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" />
    </svg>
  );
}
function IconFaceScan() {
  return (
    <svg {...svgBase}>
      <path d="M4 8V6a2 2 0 0 1 2-2h2M20 8V6a2 2 0 0 0-2-2h-2M4 16v2a2 2 0 0 0 2 2h2M20 16v2a2 2 0 0 1-2 2h-2" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7.5 18a6.5 6.5 0 0 1 9 0" />
    </svg>
  );
}
function IconFlask() {
  return (
    <svg {...svgBase}>
      <path d="M10 2h4M10 2v5l-5 8a4 4 0 0 0 3.4 6h7.2A4 4 0 0 0 19 15l-5-8V2" />
      <path d="M7.5 14h9" />
    </svg>
  );
}
function IconLink() {
  return (
    <svg {...svgBase}>
      <path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 1 0-7.07-7.07l-1.2 1.2" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 1 0 7.07 7.07l1.2-1.2" />
    </svg>
  );
}
function IconSunCloud() {
  return (
    <svg {...svgBase}>
      <circle cx="7" cy="8" r="3" /><path d="M7 1v2M7 13v2M1 8h2M11 8h2M3.5 3.5l1.4 1.4M9.1 9.1l1.4 1.4" />
      <path d="M13 19h5a3 3 0 0 0 0-6 4 4 0 0 0-7-1.5" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg {...svgBase}>
      <path d="M12 2l7 3v6c0 5-3.5 8.5-7 9-3.5-.5-7-4-7-9V5l7-3Z" />
    </svg>
  );
}
function IconHair() {
  return (
    <svg {...svgBase}>
      <path d="M12 2c0 7-4 9-4 15M12 2c0 7 4 9 4 15M12 2v15" />
    </svg>
  );
}
function IconMakeup() {
  return (
    <svg {...svgBase}>
      <path d="M3 12s3-4 9-4 9 4 9 4-3 4-9 4-9-4-9-4Z" />
      <path d="M7 12s2 1 5 1 5-1 5-1" />
    </svg>
  );
}

function IconBook() {
  return (
    <svg {...svgBase}>
      <path d="M4 5a2 2 0 0 1 2-2h11a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2V5Z" />
      <path d="M6 3v14" />
    </svg>
  );
}
function IconChat() {
  return (
    <svg {...svgBase}>
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}
function IconPremium() {
  return (
    <svg {...svgBase}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}
function IconLock() {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// Status badge — maps to brand pill variants
type BadgeVariant = 'live' | 'soon' | 'testing' | 'premium';
function Badge({ label, variant = 'live', icon }: { label: string; variant?: BadgeVariant; icon?: React.ReactNode }) {
  const cls: Record<BadgeVariant, string> = {
    live:    'pill pill--teal',
    soon:    'pill',
    testing: 'pill pill--coral',
    premium: 'pill pill--coral',
  };
  const showDot: Record<BadgeVariant, string | null> = {
    live:    'dot',
    soon:    null,
    testing: 'dot dot--coral',
    premium: 'dot dot--coral',
  };
  const dot = showDot[variant];
  return (
    <span className={cls[variant]}>
      {icon ?? (dot && <span className={dot} />)}
      {label}
    </span>
  );
}

// Tool card — hub__card in compact row layout
function Card({
  icon, title, badge, desc, to, disabled, onClick,
}: {
  icon: React.ReactNode;
  title: string;
  badge: React.ReactNode;
  desc: string;
  to?: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  const nav = useNavigate();

  const handleClick = () => {
    if (onClick) { onClick(); return; }
    if (to && !disabled) nav(to);
  };

  const cardCls = [
    'hub__card hub__card--app',
    disabled && !onClick ? 'hub__card--muted' : '',
  ].filter(Boolean).join(' ');

  const iconCls = disabled && !onClick ? 'hub__icon hub__icon--muted' : 'hub__icon';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled && !onClick}
      className={cardCls}
    >
      <div className={iconCls}>{icon}</div>
      <div className="hub__body">
        <div className="hub__head">
          <span className="hub__name">{title}</span>
          {badge}
        </div>
        <p className="hub__desc">{desc}</p>
      </div>
    </button>
  );
}

export default function NewHubPage(): React.JSX.Element {
  const { credits, canAfford } = useUser();
  const nav = useNavigate();

  const isPremium = React.useMemo(() => {
    if (!credits?.plan_name) return false;
    const p = credits.plan_name.toLowerCase();
    return p.includes('pro') || p.includes('plus') || p.includes('premium');
  }, [credits]);

  const premiumBadge = isPremium
    ? <Badge label="Premium" variant="premium" />
    : <Badge label="Premium" variant="premium" icon={<IconLock />} />;

  // Tier-gated features (premium face / hair / makeup) require Plus+. The credit
  // affordability check is enforced server-side too; we mirror it for the badge.
  const hairUnlocked = isPremium && canAfford('hair_analysis');
  const makeupUnlocked = isPremium && canAfford('makeup_analysis');

  return (
    <div className="stack-5">
      <div className="stack-2">
        <h2 className="t-h2">Start something <em className="t-italic t-deep">new.</em></h2>
        <p className="t-small t-mute">Scan a product, analyze an ingredient, or read your skin.</p>
      </div>

      <div className="g-3">
        {/* ── AI Analysis ── */}
        <Card
          icon={<IconPremium />}
          title="Premium Skin Read"
          badge={premiumBadge}
          desc="A detailed appearance read and a routine."
          to={isPremium ? '/premium-analysis/new' : undefined}
          onClick={!isPremium ? () => { nav('/plans'); } : undefined}
        />
        <Card
          icon={<IconHair />}
          title="Hair Analysis"
          badge={hairUnlocked ? <Badge label="Premium" variant="premium" /> : <Badge label="Premium" variant="premium" icon={<IconLock />} />}
          desc="AI hairstyle, length & color recommendations tailored to your face shape."
          to={hairUnlocked ? '/hair-analysis/new' : undefined}
          onClick={!hairUnlocked ? (() => { nav(isPremium ? '/wallet' : '/plans'); }) : undefined}
        />
        <Card
          icon={<IconMakeup />}
          title="Makeup Analysis"
          badge={makeupUnlocked ? <Badge label="Premium" variant="premium" /> : <Badge label="Premium" variant="premium" icon={<IconLock />} />}
          desc="Personalized foundation, eye & blush picks based on your features."
          to={makeupUnlocked ? '/makeup-analysis/new' : undefined}
          onClick={!makeupUnlocked ? (() => { nav(isPremium ? '/wallet' : '/plans'); }) : undefined}
        />

        {/* ── Scans & Tools ── */}
        <Card
          icon={<IconFaceScan />}
          title="Skin Type & Appearance"
          badge={<Badge label="Live" variant="live" />}
          desc="Spot oily, dry, or normal skin and review common appearance patterns."
          to="/analyses/new"
        />
        <Card
          icon={<IconFlask />}
          title="Ingredient & Sensitivity Check"
          badge={<Badge label="Live" variant="live" />}
          desc="Flags ingredients commonly linked to irritation."
          to="/scanner"
        />
        <Card
          icon={<IconLink />}
          title="Product Compatibility"
          badge={<Badge label="Live" variant="live" />}
          desc="Check if two products work well together with your skin profile."
          to="/compatibility"
        />
        <Card
          icon={<IconShield />}
          title="Product Comparison"
          badge={<Badge label="Live" variant="live" />}
          desc="Compare two products side-by-side to find the better fit."
          to="/comparison"
        />
        <Card
          icon={<IconSunCloud />}
          title="UV & Weather"
          badge={<Badge label="Live" variant="live" />}
          desc="Daily environment tips for your skin."
          to="/weather"
        />

        {/* ── Resources ── */}
        <Card
          icon={<IconBox />}
          title="Products Database"
          badge={<Badge label="Live" variant="live" />}
          desc="Browse and search the full collection of analysed skincare products."
          to="/products"
        />
        <Card
          icon={<IconBook />}
          title="Ingredients Database"
          badge={<Badge label="Live" variant="live" />}
          desc="Search ingredients and read their functions, risks, and evidence."
          to="/ingredients"
        />
        <Card
          icon={<IconChat />}
          title="AI Skincare Assistant"
          badge={<Badge label="Dev" variant="testing" />}
          desc="Chat with AI for routine tips, product picks, and plain explanations."
          to="/assistant"
        />
      </div>
    </div>
  );
}
